// @ts-check
// =============================================================================
// js/ui/deck-list.js — the per-hero deck picker overlay.
//
// WHAT THIS IS (deck-builder.md "deck-list")
//   The port of RenderDeckslist (:1866): tap a hero in the lobby (or the
//   builder) and pick among your saved decks for that hero, the prebuilt, or
//   "+ New Deck". Shared by the lobby and the deck builder.
//
// BEHAVIOR (kept from the old client)
//   - Saved decks: Select (becomes the loaded deck) / Edit (opens the builder).
//   - Prebuilt: Select / View (both select; View also opens the builder
//     read-only — the old client just selected, we do the same for Select and
//     route View into the builder's viewer).
//   - "+ New Deck": paid users only — free/guest get the supporter upsell
//     (old :2142; note the old gate was realUser = premium).
//   - Deck choice persists to localStorage "Deck Index" (session restore).
// =============================================================================

import { store, setSlice } from '../core/store.js';
import { navigate } from '../core/router.js';
import { can } from '../app/session.js';
import { showUpsell } from './upsell.js';
import { escapeHtml } from './modal.js';
import { VerifyDeck } from '../rules.js';

/**
 * Does this saved deck still pass the current rules? A deck built before a
 * rebalance can become an illegal size/composition; the server refuses to duel
 * with it, so we flag it here too. VerifyDeck returns true or a reason string,
 * and can throw on unknown card ids — treat any non-true / throw as invalid.
 * @param {any} deck
 * @returns {boolean}
 */
export function deckIsCurrentlyValid(deck) {
	// If the card/hero tables aren't loaded we can't judge — don't false-flag.
	if (!store.cards?.length || !store.heros?.length) {
		return true;
	}
	try {
		return VerifyDeck(deck, store.cards, store.heros) === true;
	} catch {
		return false;
	}
}

/** Hero id -> asset name (the old heroCards list :56). */
export const HERO_ASSET_NAMES = [
	'john', 'luckfoot', 'danger', 'vargen', 'nibnab', 'auri',
	'tagrix', 'draconos', 'snivels', 'solarix', 'elenya', 'throne',
];

/**
 * Remember + broadcast the selected deck.
 * @param {any} deck        The deck object (user deck or prebuilt).
 * @param {number|string} index  Its request index (slot < 100, prebuilt >= 100).
 * @returns {void}
 */
export function selectDeck(deck, index) {
	// The request layer needs the index; keep it on the object (old NewDeck
	// seeded .index on user decks; prebuilts carry theirs from the server).
	deck.index = Number(index);
	setSlice('loadedDeck', deck);
	localStorage.setItem('Deck Index', String(index));
}

/**
 * Open the deck picker for a hero.
 * @param {number|string} heroId  0–11.
 * @returns {void}
 */
export function openDeckList(heroId) {
	document.getElementById('decklist-popup')?.remove();

	const userData = store.userData;
	if (!userData) return;
	const heroName = HERO_ASSET_NAMES[Number(heroId)] || String(heroId);

	// The hero's prebuilt deck (defaultdecks carry .index/.hero/.nickname).
	const prebuilt = (userData.defaultdecks || []).find((d) => d && String(d.hero) === String(heroId)) || null;

	// Saved decks for this hero + the first free slot for "+ New Deck".
	if (!Array.isArray(userData.decks)) userData.decks = [];
	let newIdx = userData.decks.findIndex((d) => d === false);
	if (newIdx === -1) newIdx = userData.decks.length;

	// The deck currently loaded (if any) gets its row lit, so the popup
	// answers "which one am I on?" at a glance. Deck indexes are globally
	// unique (user slots 0–99, prebuilts 100+), so the index alone matches.
	const selectedIndex = store.loadedDeck ? Number(store.loadedDeck.index) : NaN;

	const deckRows = [];
	for (let i = 0; i < userData.decks.length; i++) {
		const deck = userData.decks[i];
		if (!deck || String(deck.hero) !== String(heroId)) continue;
		const name = escapeHtml(deck.nickname?.trim() || 'Unnamed Deck');
		const invalid = !deckIsCurrentlyValid(deck);
		const selected = selectedIndex === i;
		const badge = invalid
			? `<span class="decklistInvalid" title="The game was rebalanced since you built this deck. Edit it to make it legal again.">⚠ Rebalanced — update needed</span>`
			: '';
		deckRows.push(`
			<li class="decklistItem${invalid ? ' decklistItem--invalid' : ''}${selected ? ' decklistItem--selected' : ''}" data-deck-index="${i}"${selected ? ' title="Your currently selected deck"' : ''}>
				<span class="decklistItemName">${name}</span>
				${badge}
				<span class="decklistItemStats">${deck.meta_plays || 0} plays • ${deck.meta_wins || 0} wins • ${deck.meta_losses || 0} losses</span>
				<span class="decklistFlexSpacer"></span>
				<button class="decklistButton decklistButton--select js-deck-select">Select</button>
				<button class="decklistButton decklistButton--edit js-deck-edit">Edit</button>
			</li>`);
	}

	const prebuiltSelected = !!prebuilt && selectedIndex === Number(prebuilt.index);
	const prebuiltRow = prebuilt
		? `
			<li class="decklistItem${prebuiltSelected ? ' decklistItem--selected' : ''}" data-prebuilt="1"${prebuiltSelected ? ' title="Your currently selected deck"' : ''}>
				<span class="decklistItemName">Prebuilt (${escapeHtml(heroName)})</span>
				<span class="decklistFlexSpacer"></span>
				<button class="decklistButton decklistButton--prebuiltSelect js-prebuilt-select">Select</button>
				<button class="decklistButton decklistButton--prebuiltView js-prebuilt-view">View</button>
			</li>`
		: '';

	const popup = document.createElement('div');
	popup.id = 'decklist-popup';
	popup.className = 'is-open';
	popup.innerHTML = `
		<div class="decklistOverlay"></div>
		<div class="decklistPanel">
			<div class="decklistHeader">
				<img class="decklistHeroImage" src="/leaders/${heroName}.png" alt="${escapeHtml(heroName)}">
			</div>
			<div class="decklistBody">
				<span class="decklistTitle">DECKS FOR ${escapeHtml(heroName).toUpperCase()}</span>
				<ul class="decklistList">
					${prebuiltRow}
					${deckRows.join('')}
				</ul>
				<div style="display:flex;justify-content:flex-end;width:100%;margin-top:0.25rem;">
					<button id="btn-new-deck" class="decklistButton decklistButton--create">+ New Deck</button>
				</div>
			</div>
		</div>`;

	const close = () => {
		popup.remove();
		window.removeEventListener('hashchange', close);
	};
	popup.querySelector('.decklistOverlay')?.addEventListener('click', close);
	// The popup lives on document.body — navigating away must clear it too.
	window.addEventListener('hashchange', close);

	// Prebuilt: Select loads it; View also opens the builder to browse it.
	if (prebuilt) {
		popup.querySelector('.js-prebuilt-select')?.addEventListener('click', () => {
			selectDeck(prebuilt, prebuilt.index);
			close();
		});
		popup.querySelector('.js-prebuilt-view')?.addEventListener('click', () => {
			selectDeck(prebuilt, prebuilt.index);
			close();
			navigate('deck/' + prebuilt.index);
		});
	}

	// Saved decks.
	for (const row of popup.querySelectorAll('.decklistItem[data-deck-index]')) {
		const index = Number(row.getAttribute('data-deck-index'));
		row.querySelector('.js-deck-select')?.addEventListener('click', () => {
			selectDeck(store.userData.decks[index], index);
			close();
		});
		row.querySelector('.js-deck-edit')?.addEventListener('click', () => {
			close();
			navigate('deck/' + index);
		});
	}

	// New deck: verified accounts only (decision #9: Cheap KEEPS deckbuilding
	// — the old client gated on premium, but the server now accepts Save Deck
	// from any subscriber; only guests are locked out).
	popup.querySelector('#btn-new-deck')?.addEventListener('click', () => {
		if (can('saveDeck')) {
			close();
			navigate(`deck/new-${heroId}-${newIdx}`);
		} else {
			showUpsell('saveDeck');
		}
	});

	document.body.appendChild(popup);
}
